/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { Link, createSearchParams, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { PageLoader } from 'components/PageLoader'
import { TabsItem } from 'types/tabsItem'
import { RouteValue, cmsRoutes, useLink } from 'routes/cmsRoutes'
import TabsNavigation from 'components/TabsNavigation'
import { GET_USER } from 'queries/users'
import { findOneUser, findOneUserVariables } from '__generatedTypes__/findOneUser'
import { RolesEnum } from '__generatedTypes__/globalTypes'
import { authService } from 'utils/services/auth'
import { useAuthContext } from 'utils/auth'
import { ImpersonateButton } from '../../../components/Impersonate'
import * as classes from './styles'
import { UserInfo } from './components/UserInfo'
import { UserManagement } from './components/UserManagement'
import Memberships from './components/Memberships'
import MembershipUsage from './components/MembershipUsage'
import { Warning } from './components/Warning'
import { Items } from './components/Items'

export const INFO_TAB = {
  id: 'user-info',
  title: 'User Info',
}

export const MANAGEMENT_TAB = {
  id: 'management',
  title: 'User management',
}

export const MEMBERSHIPS_TAB = {
  id: 'memberships',
  title: 'Memberships',
}

export const ITEMS_TAB = {
  id: 'items',
  title: 'Items',
}

export const MEMBERSHIP_USAGE_TAB = {
  id: 'membership-usage',
  title: 'Membership usage',
}

export const WARNING_TAB = {
  id: 'warning',
  title: 'Warning',
}

const TABS = [INFO_TAB, MANAGEMENT_TAB, MEMBERSHIPS_TAB, ITEMS_TAB, MEMBERSHIP_USAGE_TAB, WARNING_TAB]

export const QUERY_PARAM = 'tab'

export const Edit = () => {
  const { userId } = useParams()
  const location = useLocation()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const [isOwnAccount, setIsOwnAccount] = useState<boolean | null>(null)

  const { isSuperAdmin, isAdmin } = useAuthContext()

  const { data, loading, refetch } = useQuery<findOneUser, findOneUserVariables>(GET_USER, {
    variables: {
      id: String(userId),
    },
    skip: isOwnAccount === null,
    onCompleted: (data) => {
      if (isOwnAccount || isSuperAdmin) return

      if (data.findOneUser?.role === RolesEnum.SuperAdmin) {
        // admin trying to edit super admin
        navigate(cmsRoutes.USER_LIST)
      }
    },
  })

  useEffect(() => {
    ;(async () => {
      const currentUserId = await authService.getUserId()
      setIsOwnAccount(currentUserId === userId)
    })()
  }, [])

  const user = data?.findOneUser

  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(QUERY_PARAM)

    return TABS.find(({ id }) => id === tabId) || INFO_TAB
  })

  const handleChangeTab = (activeTab: TabsItem) => {
    navigate(location.pathname as RouteValue, undefined, {
      searchParams: createSearchParams({ [QUERY_PARAM]: activeTab.id }),
      replace: true,
    })
    setActiveTab(activeTab)
    refetch()
  }

  if ((loading && !user) || isOwnAccount === null) {
    return <PageLoader />
  }

  if (!user) {
    return <Typography>Something went wrong!</Typography>
  }

  const renderTab = () => {
    if (activeTab.id === INFO_TAB.id) {
      return <UserInfo user={user} isOwnAccount={isOwnAccount} refetchUser={refetch} />
    }

    if (activeTab.id === MANAGEMENT_TAB.id) {
      return <UserManagement user={user} />
    }

    if (activeTab.id === MEMBERSHIPS_TAB.id) {
      return <Memberships user={user} refetchUser={refetch} />
    }

    if (activeTab.id === MEMBERSHIP_USAGE_TAB.id) {
      return <MembershipUsage user={user} />
    }

    if (activeTab.id === WARNING_TAB.id) {
      return <Warning user={user} />
    }

    if (activeTab.id === ITEMS_TAB.id) {
      return <Items user={user} />
    }

    return null
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Edit User
        {isAdmin && <ImpersonateButton user={user} />}
      </Typography>

      <div>
        <Typography variant="title4" css={classes.userLogin}>
          User Login:{' '}
          <Link to={`mailto:${user.email}`} css={classes.emailLink}>
            {user.email}
          </Link>
        </Typography>
      </div>

      <TabsNavigation css={classes.tabs} tabs={TABS} value={activeTab} onChange={handleChangeTab} />

      {renderTab()}
    </section>
  )
}
